/*
  Draws a short centred progress bar for a horizontally scrolling container.
  The thumb width reflects how much of the row is visible, and its offset
  reflects the scroll position. Hides itself when there is nothing to scroll.
*/
if (!customElements.get('scroll-progress')) {
  class ScrollProgress extends HTMLElement {
    /*
      A custom element upgrades as soon as its opening tag is parsed, before
      its own children exist. Reading the thumb here directly returns null
      and the element gives up, leaving the bar at its CSS default: a full
      width thumb that never moves. Waiting for the document defers setup
      until the markup is actually there.
    */
    connectedCallback() {
      if (document.readyState === 'loading') {
        this.onReady = () => this.init();
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
        return;
      }

      this.init();
    }

    init() {
      this.thumb = this.querySelector('[data-scroll-progress-thumb]');
      this.scroller = document.getElementById(this.dataset.scroller);
      if (!this.scroller || !this.thumb) return;

      this.ticking = false;

      // The last width written, so it is only set again when it actually
      // changes. See update() for why that matters.
      this.lastWidth = null;

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

      // Panels are hidden with visibility, not display, so widths are always
      // measurable, but a resize still changes how many cards fit.
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scroller);

      /*
        Cards can arrive after this element connects: Recently viewed fetches
        its row and injects it, and a tabbed section clones a tab in the
        first time it is chosen. That changes the scroll width but not the
        row's own size, so the resize observer never fires.

        Watching the child list covers it. Not the subtree: card markup
        changes on a colour swap and re-measuring on every one of those is
        work for no change in width.
      */
      this.mutationObserver = new MutationObserver(this.update);
      this.mutationObserver.observe(this.scroller, { childList: true });

      /*
        Images without intrinsic dimensions settle after they load, and a web
        font can reflow a card. Both change the scroll width without touching
        the row's own size, so one late re-measure catches what the observers
        miss.
      */
      window.addEventListener('load', this.update, { once: true });

      this.update();
    }

    disconnectedCallback() {
      if (this.onReady) document.removeEventListener('DOMContentLoaded', this.onReady);
      if (this.scroller && this.onScroll) this.scroller.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.mutationObserver) this.mutationObserver.disconnect();
      if (this.update) window.removeEventListener('load', this.update);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      const { scrollWidth, clientWidth, scrollLeft } = this.scroller;
      const maxScroll = scrollWidth - clientWidth;

      // Nothing to scroll, so a bar would say something untrue. One pixel of
      // slack, because sub pixel layout leaves a fraction over on rows that
      // fit exactly.
      if (maxScroll <= 1) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');

      const thumbRatio = clientWidth / scrollWidth;
      const progress = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);

      /*
        The width only changes when the row is resized or its contents
        change, not while scrolling, so it is written only when it differs.
        Setting any layout property every frame forces the browser to
        recalculate the page even when the value is identical.
      */
      const width = thumbRatio * 100;
      if (width !== this.lastWidth) {
        this.thumb.style.width = width + '%';
        this.lastWidth = width;
      }

      /*
        Movement is a transform, not an inset.

        Both look the same, but an inset is a layout property: changing it
        makes the browser recompute the geometry of the page on the main
        thread, on every frame of every scroll. A transform is handled by
        the compositor instead, which is what Lighthouse means when it
        reports a non composited animation.

        The percentage is of the thumb's own width rather than the track's,
        which is what a transform percentage refers to. The thumb travels
        the track minus itself, so as a proportion of its own width that is
        (1 - ratio) / ratio.
      */
      const travel = ((1 - thumbRatio) / thumbRatio) * 100 * progress;
      this.thumb.style.transform = `translateX(${travel}%)`;
    }
  }

  customElements.define('scroll-progress', ScrollProgress);
}
